/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Lock, Smartphone, Eye, EyeOff, 
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Loader2, CheckCircle2, ShieldCheck,
  ArrowRight, AlertCircle, Truck
} from 'lucide-react';
import { otpService } from '../api/otp';
import { authService } from '../api/auth';
import { API_URL } from '../api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { resumePendingAuthActionAfterLogin } from '../utils/runPendingAuthAction';
import { notifyAuthStorageChanged } from '../api/auth';
import { getImageUrl } from '../utils/imageUtils';

const REGISTER_BENEFITS = [
  {
    icon: Smartphone,
    title: 'Quick sign-up',
    desc: 'Verify your mobile with OTP in under a minute.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure checkout',
    desc: 'Shop with confidence — your account stays protected.',
  },
  {
    icon: Truck,
    title: 'Order tracking',
    desc: 'Track deliveries and manage addresses in one place.',
  },
] as const;

const inputBase =
  'w-full text-sm text-slate-900 bg-white border border-slate-200 rounded-lg shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors disabled:bg-slate-50 disabled:text-slate-500';

// Password strength checker
const getPasswordStrength = (password: string): { strength: number; message: string; color: string } => {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  const messages = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  
  return { 
    strength, 
    message: messages[strength], 
    color: colors[strength] 
  };
};

// Validate password requirements
const validatePassword = (password: string): string[] => {
  const errors: string[] = [];
  if (password.length < 8) errors.push('At least 8 characters');
  if (!/[A-Z]/.test(password)) errors.push('At least 1 uppercase letter');
  if (!/[0-9]/.test(password)) errors.push('At least 1 number');
  if (!/[^A-Za-z0-9]/.test(password)) errors.push('At least 1 special character');
  return errors;
};

const Register = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { addToCart: addToCartLocal } = useCart();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    referralCode: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState('');
  const [firebaseIdToken, setFirebaseIdToken] = useState<string | undefined>();
  const [countdown, setCountdown] = useState(0);

  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, message: 'Weak', color: 'bg-red-500' });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [storeBrand, setStoreBrand] = useState({ name: 'NLS Store', logo: '' });
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  useEffect(() => {
    setPasswordStrength(getPasswordStrength(formData.password));
    setPasswordErrors(validatePassword(formData.password));
  }, [formData.password]);

  useEffect(() => {
    fetch(`${API_URL}/admin/ultra-settings/public/store`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && data.data) {
          setLogoLoadFailed(false);
          setStoreBrand({
            name: data.data.storeName || 'NLS Store',
            logo: getImageUrl(data.data.storeLogo || ''),
          });
        }
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      setOtpVerified(false);
      setFirebaseIdToken(undefined);
      setEnteredOtp('');
      setOtpSent(false);
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    if (!formData.firstName.trim()) errors.push('First name is required');
    if (!formData.lastName.trim()) errors.push('Last name is required');
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    if (!formData.phone) {
      errors.push('Phone number is required for account verification');
    } else if (formData.phone.length !== 10) {
      errors.push('Phone number must be 10 digits');
    }
    if (passwordErrors.length > 0) {
      errors.push('Password does not meet security requirements');
    }
    if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }
    if (!formData.agreeToTerms) {
      errors.push('You must agree to the Terms & Conditions');
    }
    if (errors.length > 0) {
      setError(errors.join('. '));
      return false;
    }
    return true;
  };

  const handleSendOtp = async () => {
    setError('');
    setSuccess('');
    if (!formData.phone || formData.phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const result = await otpService.sendOTP(formData.phone);
      if (result.success) {
        setOtpSent(true);
        setOtpVerified(false);
        setFirebaseIdToken(undefined);
        setEnteredOtp('');
        setCountdown(30);
        setSuccess(`OTP sent to +91 ${formData.phone}. Please check your phone.`);
      } else {
        setError(result.message || 'Failed to send OTP');
      }
    } catch (err: any) {
      setError(err.message || 'Error sending OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      const result = await otpService.resendOTP(formData.phone);
      if (result.success) {
        setCountdown(30);
        setSuccess('OTP resent successfully. Please check your phone.');
      } else {
        setError(result.message || 'Failed to resend OTP');
      }
    } catch (err: any) {
      setError(err.message || 'Error resending OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setSuccess('');
    if (!enteredOtp || enteredOtp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const result = await otpService.verifyOTP(formData.phone, enteredOtp);
      if (result.success) {
        const pc = await otpService.fetchProviderConfig();
        if (
          pc.provider === 'firebase' &&
          process.env.NODE_ENV === 'production' &&
          !result.firebaseIdToken
        ) {
          setError(
            'Could not complete Firebase verification. Request a new OTP and try again.'
          );
          return;
        }
        setFirebaseIdToken(result.firebaseIdToken);
        setOtpVerified(true);
        setSuccess('Phone number verified successfully! You can now complete registration.');
      } else {
        setError(result.message || 'Invalid OTP');
      }
    } catch (err: any) {
      setError(err.message || 'Error verifying OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (!otpVerified) {
      setError('Please verify your phone number with OTP before registering');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use the register-verify endpoint which creates user after OTP verification
      const result = await otpService.registerWithOTP({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email.trim() || undefined,
        phone: formData.phone,
        password: formData.password,
        otp: enteredOtp,
        referralCode: formData.referralCode || undefined,
        firebaseIdToken,
      });

      if (result.success) {
        setSuccess('Registration successful! Redirecting...');
        authService.saveAuthData(result.token, result.user);
        notifyAuthStorageChanged();
        checkAuth();

        const redirectPath = await resumePendingAuthActionAfterLogin({
          addToCartLocal,
          navigate: (path, options) => navigate(path, { ...(options || {}), replace: true }),
        });

        setTimeout(() => {
          navigate(redirectPath || '/', { replace: true });
        }, 800);
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const progressSteps = [
    { label: 'Details', done: !!(formData.firstName && formData.lastName) },
    { label: 'Verify phone', done: otpVerified },
    { label: 'Secure account', done: passwordErrors.length === 0 && formData.password.length > 0 },
  ];

  const showSidebarLogo = Boolean(storeBrand.logo) && !logoLoadFailed;

  return (
    <div className="min-h-screen bg-slate-50 pt-[72px] sm:pt-20">
      {/* Desktop: fixed sidebar from below site header to viewport bottom */}
      <aside
        aria-hidden={false}
        className="fixed left-0 top-[72px] bottom-0 z-0 hidden w-[380px] flex-col bg-slate-900 text-white border-r border-slate-800 sm:top-20 lg:flex xl:w-[400px]"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950" />
        <div className="pointer-events-none absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_70%_40%_at_100%_0%,rgba(220,38,38,0.22),transparent_55%)]" />

        <div className="relative z-10 flex h-full min-h-0 flex-col">
          <div className="shrink-0 px-8 pb-6 pt-8 xl:px-10 xl:pt-9">
            <Link to="/" className="inline-flex items-center">
              {showSidebarLogo ? (
                <img
                  src={storeBrand.logo}
                  alt={storeBrand.name}
                  className="h-10 w-auto max-w-[200px] object-contain"
                  onError={() => setLogoLoadFailed(true)}
                />
              ) : (
                <span className="text-xl font-bold tracking-tight text-white">{storeBrand.name}</span>
              )}
            </Link>
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto px-8 pb-8 xl:px-10">
            <h1 className="text-[1.35rem] font-bold leading-snug tracking-tight text-white xl:text-2xl">
              Shop with one account
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Join {storeBrand.name} for faster checkout, order updates, and saved addresses.
            </p>

            <ul className="mt-8 space-y-6">
              {REGISTER_BENEFITS.map(({ icon: Icon, title, desc }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/12 bg-white/5 text-primary-300">
                    <Icon className="h-[0.95rem] w-[0.95rem]" strokeWidth={2} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-100">{title}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-slate-500">{desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="shrink-0 border-t border-white/10 px-8 py-4 text-[11px] text-slate-600 xl:px-10">
            © {new Date().getFullYear()} {storeBrand.name}
          </p>
        </div>
      </aside>

      <div className="relative z-[1] flex flex-col lg:min-h-[calc(100vh-5rem)] lg:pl-[380px] xl:pl-[400px]">
        {/* Form panel */}
        <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8 lg:px-12 lg:py-12">
          <div className="w-full max-w-[480px]">
            <div className="lg:hidden mb-8 text-center">
              {storeBrand.logo ? (
                <img src={storeBrand.logo} alt={storeBrand.name} className="h-9 mx-auto mb-4 object-contain" />
              ) : (
                <p className="text-lg font-bold text-slate-900 mb-2">{storeBrand.name}</p>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Create your account</h2>
              <p className="mt-2 text-slate-600 text-sm sm:text-base">
                Already registered?{' '}
                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                  Sign in
                </Link>
              </p>
            </div>

            <div className="mb-8 flex items-center gap-2">
              {progressSteps.map((step, i) => (
                <React.Fragment key={step.label}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        step.done ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {step.done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium truncate hidden sm:inline ${step.done ? 'text-slate-900' : 'text-slate-400'}`}>
                      {step.label}
                    </span>
                  </div>
                  {i < progressSteps.length - 1 && <div className="flex-1 h-px bg-slate-200 min-w-[12px]" />}
                </React.Fragment>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-200/50 p-6 sm:p-8">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-6 pb-6 border-b border-slate-100">
                <ShieldCheck className="w-4 h-4 text-primary-600 shrink-0" />
                <span>Your information is protected and never shared without consent.</span>
              </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <section className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Personal details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-1.5">First name</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input id="firstName" name="firstName" type="text" required autoComplete="given-name" className={`${inputBase} pl-10 py-2.5`} placeholder="First name" value={formData.firstName} onChange={handleChange} />
                </div>
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-1.5">Last name</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input id="lastName" name="lastName" type="text" required autoComplete="family-name" className={`${inputBase} pl-10 py-2.5`} placeholder="Last name" value={formData.lastName} onChange={handleChange} />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input id="email" name="email" type="email" autoComplete="email" className={`${inputBase} pl-10 py-2.5`} placeholder="you@example.com" value={formData.email} onChange={handleChange} />
              </div>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Phone verification</p>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1.5">
                Mobile number <span className="text-primary-600">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-medium">+91</span>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="10-digit mobile"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={10}
                    className={`${inputBase} pl-12 pr-10 py-2.5 ${
                      otpVerified ? 'border-emerald-500 bg-emerald-50/50' : ''
                    }`}
                    required
                    disabled={otpVerified}
                  />
                  {otpVerified && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-600" />
                  )}
                </div>
                {!otpVerified && (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={loading || formData.phone.length !== 10 || otpSent}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
                  >
                    {loading && !otpSent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                    {otpSent ? 'OTP sent' : 'Send OTP'}
                  </button>
                )}
              </div>
              {formData.phone && formData.phone.length !== 10 && (
                <p className="text-xs text-red-600 mt-1.5">Enter a valid 10-digit number</p>
              )}

              {otpSent && !otpVerified && (
                <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Enter 6-digit OTP</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="• • • • • •"
                      value={enteredOtp}
                      onChange={(e) => setEnteredOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className={`${inputBase} flex-1 text-center text-xl tracking-[0.4em] font-mono py-3`}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={loading || enteredOtp.length !== 6}
                      className="px-5 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-200 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Verify'}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-slate-600">
                    <span>{countdown > 0 ? `Resend in ${countdown}s` : "Didn't receive code?"}</span>
                    {countdown === 0 && (
                      <button type="button" onClick={handleResendOtp} disabled={loading} className="font-semibold text-primary-600 hover:text-primary-700">
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              )}
              {otpVerified && (
                <p className="mt-2 flex items-center gap-2 text-sm font-medium text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" />
                  Mobile verified
                </p>
              )}
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Password</p>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">Create password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className={`${inputBase} pl-10 pr-11 py-2.5`}
                  placeholder="Min. 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{ width: `${(passwordStrength.strength / 4) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-600">{passwordStrength.message}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    {[
                      { check: formData.password.length >= 8, label: '8+ characters' },
                      { check: /[A-Z]/.test(formData.password), label: 'Uppercase' },
                      { check: /[0-9]/.test(formData.password), label: 'Number' },
                      { check: /[^A-Za-z0-9]/.test(formData.password), label: 'Special char' },
                    ].map((req) => (
                      <div key={req.label} className={`flex items-center gap-1.5 ${req.check ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {req.check ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-slate-300" />}
                        {req.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className={`${inputBase} pl-10 pr-11 py-2.5 ${
                    formData.confirmPassword && formData.password !== formData.confirmPassword
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/15'
                      : formData.confirmPassword && formData.password === formData.confirmPassword
                        ? 'border-emerald-500'
                        : ''
                  }`}
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle confirm password">
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-600 mt-1.5">Passwords do not match</p>
              )}
            </div>
          </section>

          <div>
            <label htmlFor="referralCode" className="block text-sm font-medium text-slate-700 mb-1.5">
              Referral code <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              id="referralCode"
              name="referralCode"
              type="text"
              className={`${inputBase} px-4 py-2.5 uppercase`}
              placeholder="e.g. NLSABC123"
              value={formData.referralCode}
              onChange={handleChange}
            />
          </div>

          <label htmlFor="agree-terms" className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100 cursor-pointer">
            <input
              id="agree-terms"
              name="agreeToTerms"
              type="checkbox"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              required
            />
            <span className="text-sm text-slate-600 leading-relaxed">
              I agree to the{' '}
              <Link to="/terms-of-service" className="font-semibold text-primary-600 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="font-semibold text-primary-600 hover:underline">Privacy Policy</Link>
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !otpVerified}
            className="w-full py-3.5 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating account…
              </>
            ) : (
              <>
                Create account
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          {!otpVerified && (
            <p className="text-center text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg py-2 px-3">
              Verify your mobile number to complete registration
            </p>
          )}
        </form>
            </div>

            <p className="mt-6 text-center text-xs text-slate-500 lg:hidden">
              Secure registration · {storeBrand.name}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Register;

